// src/utils/fourthwall.js

import { API } from "./constants";

const STOREFRONT_TOKEN =
  process.env.REACT_APP_FW_STOREFRONT_TOKEN;

const CHECKOUT_DOMAIN =
  process.env.REACT_APP_FW_CHECKOUT_DOMAIN;

const FW_API_BASE =
  "https://storefront-api.fourthwall.com/v1";

/**
 * Normalise currency so only currencies we currently
 * want to support in the storefront are used.
 */
export function normaliseCurrency(currency) {
  const value = String(currency || "")
    .trim()
    .toUpperCase();

  if (
    value === "GBP" ||
    value === "EUR" ||
    value === "USD"
  ) {
    return value;
  }

  return "USD";
}

/**
 * Public Fourthwall storefront request.
 *
 * Used only for public collection/product data.
 *
 * Currency is passed to Fourthwall so product prices
 * come back in the same currency we intend to use
 * for the cart and checkout.
 */
async function fourthwallGet(
  path,
  currency = "USD"
) {
  if (!STOREFRONT_TOKEN) {
    throw new Error(
      "Missing REACT_APP_FW_STOREFRONT_TOKEN in frontend environment."
    );
  }

  const resolvedCurrency =
    normaliseCurrency(currency);

  const separator =
    path.includes("?") ? "&" : "?";

  const url =
    `${FW_API_BASE}${path}` +
    `${separator}storefront_token=${encodeURIComponent(
      STOREFRONT_TOKEN
    )}` +
    `&currency=${encodeURIComponent(
      resolvedCurrency
    )}`;

  const res = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
    },
  });

  if (!res.ok) {
    const text = await res.text();

    throw new Error(
      `Fourthwall API ${res.status}: ${text}`
    );
  }

  return res.json();
}

/**
 * Calls our own backend merch API.
 *
 * The browser never performs Fourthwall cart writes directly.
 */
async function cartProxyFetch(
  path,
  options = {}
) {
  const res = await fetch(
    `${API}/merch${path}`,
    {
      ...options,
      headers: {
        Accept: "application/json",
        "Content-Type":
          "application/json",
        ...(options.headers || {}),
      },
      credentials: "include",
    }
  );

  if (!res.ok) {
    const text = await res.text();

    const error = new Error(
      `Cart API ${res.status}: ${text}`
    );

    error.status = res.status;

    throw error;
  }

  return res.json();
}

/**
 * Get products from a Fourthwall collection.
 */
export async function getCollectionProducts(
  collectionHandle,
  currency = "USD"
) {
  if (!collectionHandle) {
    throw new Error(
      "Missing Fourthwall collection handle."
    );
  }

  const data =
    await fourthwallGet(
      `/collections/${encodeURIComponent(
        collectionHandle
      )}/products`,
      currency
    );

  if (Array.isArray(data)) {
    return data;
  }

  if (
    Array.isArray(
      data?.results
    )
  ) {
    return data.results;
  }

  if (
    Array.isArray(
      data?.products
    )
  ) {
    return data.products;
  }

  return [];
}

/**
 * Retrieve a single Fourthwall product.
 */
export async function getProduct(
  productId,
  currency = "USD"
) {
  if (!productId) {
    throw new Error(
      "Missing Fourthwall product ID."
    );
  }

  return fourthwallGet(
    `/products/${encodeURIComponent(
      productId
    )}`,
    currency
  );
}

/**
 * Create a brand-new cart.
 *
 * Currency is sent to our backend so the backend
 * creates the Fourthwall cart in the same currency
 * being displayed in the storefront.
 */
export async function createCart(
  items,
  currency = "USD"
) {
  if (
    !Array.isArray(items) ||
    items.length === 0
  ) {
    throw new Error(
      "createCart requires at least one item."
    );
  }

  const resolvedCurrency =
    normaliseCurrency(currency);

  return cartProxyFetch(
    "/carts",
    {
      method: "POST",
      body: JSON.stringify({
        items,
        currency:
          resolvedCurrency,
      }),
    }
  );
}

/**
 * Add new items / variants to an existing cart.
 */
export async function addCartItems(
  cartId,
  items,
  currency = "USD"
) {
  if (!cartId) {
    throw new Error(
      "Missing cart ID."
    );
  }

  if (
    !Array.isArray(items) ||
    items.length === 0
  ) {
    throw new Error(
      "addCartItems requires at least one item."
    );
  }

  const resolvedCurrency =
    normaliseCurrency(currency);

  return cartProxyFetch(
    `/carts/${encodeURIComponent(
      cartId
    )}/add`,
    {
      method: "POST",
      body: JSON.stringify({
        items,
        currency:
          resolvedCurrency,
      }),
    }
  );
}

/**
 * Retrieve an existing cart.
 */
export async function getCart(
  cartId,
  currency = "USD"
) {
  if (!cartId) {
    throw new Error(
      "Missing cart ID."
    );
  }

  const resolvedCurrency =
    normaliseCurrency(currency);

  return cartProxyFetch(
    `/carts/${encodeURIComponent(
      cartId
    )}?currency=${encodeURIComponent(
      resolvedCurrency
    )}`,
    {
      method: "GET",
    }
  );
}

/**
 * Change quantity of an item already in the cart.
 */
export async function setCartItemQuantity(
  cartId,
  variantId,
  quantity,
  currency = "USD"
) {
  if (!cartId) {
    throw new Error(
      "Missing cart ID."
    );
  }

  if (!variantId) {
    throw new Error(
      "Missing variant ID."
    );
  }

  const resolvedQuantity =
    Number(quantity);

  if (
    !Number.isInteger(
      resolvedQuantity
    ) ||
    resolvedQuantity < 1 ||
    resolvedQuantity > 100
  ) {
    throw new Error(
      "Quantity must be between 1 and 100."
    );
  }

  const resolvedCurrency =
    normaliseCurrency(currency);

  return cartProxyFetch(
    `/carts/${encodeURIComponent(
      cartId
    )}/change`,
    {
      method: "POST",
      body: JSON.stringify({
        variantId,
        quantity:
          resolvedQuantity,
        currency:
          resolvedCurrency,
      }),
    }
  );
}

/**
 * Remove a variant completely from the cart.
 */
export async function removeCartItem(
  cartId,
  variantId,
  currency = "USD"
) {
  if (!cartId) {
    throw new Error(
      "Missing cart ID."
    );
  }

  if (!variantId) {
    throw new Error(
      "Missing variant ID."
    );
  }

  const resolvedCurrency =
    normaliseCurrency(currency);

  return cartProxyFetch(
    `/carts/${encodeURIComponent(
      cartId
    )}/remove`,
    {
      method: "POST",
      body: JSON.stringify({
        items: [
          {
            variantId,
          },
        ],
        currency:
          resolvedCurrency,
      }),
    }
  );
}

/**
 * Securely claim a cart for the currently
 * authenticated TOA user.
 */
export async function claimCart(
  cartId,
  accessToken
) {
  if (!cartId) {
    throw new Error(
      "Missing cart ID."
    );
  }

  if (!accessToken) {
    throw new Error(
      "You must be logged in before checkout."
    );
  }

  return cartProxyFetch(
    `/carts/${encodeURIComponent(
      cartId
    )}/claim`,
    {
      method: "POST",
      headers: {
        Authorization:
          `Bearer ${accessToken}`,
      },
      body:
        JSON.stringify({}),
    }
  );
}

/**
 * Return total number of units in the cart.
 */
export function cartItemCount(
  cart
) {
  if (
    !cart?.items ||
    !Array.isArray(
      cart.items
    )
  ) {
    return 0;
  }

  return cart.items.reduce(
    (
      total,
      item
    ) =>
      total +
      Number(
        item?.quantity || 0
      ),
    0
  );
}

/**
 * Generate the Fourthwall hosted checkout URL.
 *
 * Fourthwall supports:
 *
 * https://your-domain/checkout/?cartCurrency=GBP&cartId=...
 */
export function getCheckoutUrl(
  cartId,
  currency = "USD"
) {
  if (!cartId) {
    throw new Error(
      "Missing cart ID."
    );
  }

  if (!CHECKOUT_DOMAIN) {
    throw new Error(
      "Missing REACT_APP_FW_CHECKOUT_DOMAIN in frontend environment."
    );
  }

  const resolvedCurrency =
    normaliseCurrency(currency);

  const cleanDomain =
    CHECKOUT_DOMAIN
      .replace(
        /^https?:\/\//i,
        ""
      )
      .replace(
        /\/+$/,
        ""
      );

  const params =
    new URLSearchParams({
      cartCurrency:
        resolvedCurrency,
      cartId:
        String(cartId),
    });

  return (
    `https://${cleanDomain}/checkout/?` +
    params.toString()
  );
}